import { Button, Flex, Modal, Select, Typography } from "antd";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { setAudioDevices } from "../../slices/appStateSlice";
import { setRecordingDevice } from "../../slices/recordingsSlice";
import { fetchAudioInputDevices } from "../../utils/mediaRecorder.utils";
import { readPreferredMic, savePreferredMic } from "../../utils/micPreference";

type AudioDevice = { deviceId: string; groupId: string; label: string };

const BAR_COUNT = 15;

type MicTestModalProps = {
  open: boolean;
  onClose: () => void;
};

const MicTestModal = ({ open, onClose }: MicTestModalProps) => {
  const dispatch = useAppDispatch();
  const preferredDeviceId = useAppSelector((state) => state.recordings.recordingDevice?.deviceId);
  const [devices, setDevices] = React.useState<AudioDevice[]>([]);
  const [deviceId, setDeviceId] = React.useState<string>();
  const [level, setLevel] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const frameRef = React.useRef<number | null>(null);
  // Bumped on every stop; a getUserMedia that resolves after its session ended is discarded.
  const sessionRef = React.useRef(0);
  const preferredDeviceRef = React.useRef(preferredDeviceId);
  preferredDeviceRef.current = preferredDeviceId;

  const stopMeter = React.useCallback(() => {
    sessionRef.current += 1;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    setLevel(0);
  }, []);

  const startMeter = React.useCallback(
    async (id?: string) => {
      stopMeter();
      const session = sessionRef.current;
      setError(null);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: id ? { deviceId: { exact: id } } : true,
        });
      } catch (err) {
        if (session === sessionRef.current) {
          setError(
            (err as Error).name === "NotAllowedError"
              ? "Microphone access is blocked. Allow it in your browser's site settings and try again."
              : "We couldn't open that microphone. Try another one.",
          );
        }
        return null;
      }
      if (session !== sessionRef.current) {
        for (const track of stream.getTracks()) track.stop();
        return null;
      }
      streamRef.current = stream;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      audioContext.createMediaStreamSource(stream).connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (let i = 0; i < samples.length; i++) {
          const v = (samples[i] - 128) / 128;
          sum += v * v;
        }
        // RMS of normal speech is ~0.05-0.2; scale so that fills most of the meter.
        setLevel(Math.min(1, Math.sqrt(sum / samples.length) * 5));
        frameRef.current = requestAnimationFrame(tick);
      };
      tick();
      return stream;
    },
    [stopMeter],
  );

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      // Opening the mic first is what makes enumerateDevices() return labels.
      const remembered = readPreferredMic()?.deviceId ?? preferredDeviceRef.current;
      let stream = await startMeter(remembered);
      if (!cancelled && !stream && remembered) {
        // Remembered mic is unavailable — fall back to the default; the preference stays.
        stream = await startMeter(undefined);
      }
      if (cancelled || !stream) return;
      const list = await fetchAudioInputDevices();
      if (cancelled) return;
      setDevices(list);
      const activeId = stream.getAudioTracks()[0]?.getSettings().deviceId;
      setDeviceId(list.find((d) => d.deviceId === activeId)?.deviceId ?? list[0]?.deviceId);
    })();
    return () => {
      cancelled = true;
      stopMeter();
    };
  }, [open, startMeter, stopMeter]);

  const handleDeviceChange = (id: string) => {
    setDeviceId(id);
    startMeter(id);
  };

  const handleConfirm = () => {
    stopMeter();
    const device = devices.find((d) => d.deviceId === deviceId);
    if (device) {
      savePreferredMic(device);
      dispatch(setAudioDevices(devices));
      dispatch(setRecordingDevice(device));
    }
    onClose();
  };

  const litBars = Math.round(level * BAR_COUNT);

  return (
    <Modal
      title="Let's test your audio"
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={420}
      footer={
        <Button type="primary" onClick={handleConfirm} disabled={!deviceId}>
          Confirm
        </Button>
      }
    >
      <Flex vertical gap={8} style={{ marginTop: 16 }}>
        <Typography.Text>Select microphone</Typography.Text>
        <Select
          value={deviceId}
          onChange={handleDeviceChange}
          placeholder="No microphone found"
          options={devices.map((d, i) => ({ value: d.deviceId, label: d.label || `Microphone ${i + 1}` }))}
        />
        <Typography.Text style={{ marginTop: 16 }}>Input level</Typography.Text>
        <Flex
          gap={6}
          role="meter"
          aria-label="Microphone input level"
          aria-valuenow={litBars}
          aria-valuemax={BAR_COUNT}
        >
          {Array.from({ length: BAR_COUNT }, (_, i) => (
            <div
              key={i}
              style={{
                width: 12,
                height: 36,
                borderRadius: 6,
                background: i < litBars ? "#2e7d32" : "#e5e5e5",
                transition: "background 80ms",
              }}
            />
          ))}
        </Flex>
        <Typography.Text type={error ? "danger" : "secondary"} style={{ minHeight: 22 }}>
          {error ?? "Speak normally — the bars should move."}
        </Typography.Text>
      </Flex>
    </Modal>
  );
};

export default MicTestModal;
